import {
  Box,
  Card,
  CardContent,
  CardHeader,
  LinearProgress,
  ListItemIcon,
  Typography,
} from '@mui/material';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppColors } from 'resources/AppColors';
import { makeStyles } from '@mui/styles';
import { initializePatientContent } from 'redux/reducers/patientContentReducer';
import { useTextStyles } from 'resources/AppTexts';
import { scoreCardTexts } from 'locale/es';
import { initializePatientMessages } from 'redux/reducers/messagesReducer';
import { initializeFormsByPatient } from 'redux/reducers/patientFormsReducer';
import MessageIcon from '@mui/icons-material/Message';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWpforms } from '@fortawesome/free-brands-svg-icons';

const useStyles = makeStyles((theme) => ({
  customLinearProgress: {
    height: 6,
    borderRadius: 2,
    backgroundColor: AppColors.SECONDARY,
    '& .MuiLinearProgress-bar': {
      borderRadius: 2,
      backgroundColor: AppColors.PRIMARY,
    },
  },
}));

const ScoreCard = () => {
  const selectedPatient = useSelector(({ patient }) => patient.id);
  const patientDataObj = useSelector(({ patient }) => patient);
  const formsByPatient = useSelector(({ formsByPatient }) => formsByPatient);

  const classes = useStyles();

  const {
    data: patientContents,
    loading,
    error,
  } = useSelector(({ patientContent }) => patientContent);
  const { data: patientMessages } = useSelector(({ messages }) => messages);

  const dispatch = useDispatch();

  const textStyles = useTextStyles();

  useEffect(() => {
    if (selectedPatient && patientContents?.length === 0) {
      dispatch(initializeFormsByPatient(selectedPatient));
      dispatch(initializePatientContent(selectedPatient));
      dispatch(initializePatientMessages(patientDataObj?.user?.id));
    }
  }, [selectedPatient, dispatch]);

  const formsTotal = formsByPatient?.data?.length || 0;
  const contentsTotal = patientContents?.length || 0;
  const messagesTotal = patientMessages?.length || 0;

  const readContentsCount = patientContents.reduce(
    (acc, content) => acc + (content.visited === true ? 1 : 0),
    0
  );

  const readMessagesCount = patientMessages.reduce(
    (acc, message) => acc + (message.visited === true ? 1 : 0),
    0
  );

  const formStatusCount = formsByPatient.data.reduce(
    (acc, form) => acc + (form.status === 'FILLED' ? 1 : 0),
    0
  );

  const calculateProgress = (filled, assigned) => {
    if (assigned === 0) return 0;
    return (filled / assigned) * 100;
  };

  const cardData = [
    {
      title: scoreCardTexts.FORMS,
      count: formStatusCount,
      total: formsTotal,
      progress: calculateProgress(formStatusCount, formsTotal),
      icon: (
        <FontAwesomeIcon
          icon={faWpforms}
          size='xl'
          style={{ color: AppColors.PRIMARY, marginBottom: 8, marginTop: 4 }}
          data-cy='config'
        />
      ),
    },
    {
      title: scoreCardTexts.CONTENT,
      count: readContentsCount,
      total: contentsTotal,
      progress: calculateProgress(readContentsCount, contentsTotal),
      icon: (
        <CreateNewFolderIcon
          sx={{ fill: AppColors.PRIMARY, width: '28.8px', height: '36px' }}
          data-cy='content'
        />
      ),
    },
    {
      title: scoreCardTexts.MESSAGES,
      count: readMessagesCount,
      total: messagesTotal,
      progress: calculateProgress(readMessagesCount, messagesTotal),
      icon: (
        <MessageIcon
          sx={{ fill: AppColors.PRIMARY, width: '28.8px', height: '36px' }}
          data-cy='messages'
        />
      ),
    },
  ];

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
        }}>
        {cardData &&
          cardData.map((data) => (
            <Card key={data.title} sx={{ p: 2, m: 2, width: '25%' }}>
              <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mt: 2,
                    justifyContent: 'center',
                  }}>
                  {data.icon}
                </ListItemIcon>
                <CardHeader
                  title={data.title}
                  titleTypographyProps={{ className: textStyles.subtitle_bold }}
                  sx={{ mb: -2 }}
                />
              </Box>
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant='body' sx={{ mb: 0.5 }}>
                    {data.title === scoreCardTexts.FORMS
                      ? scoreCardTexts.FILLED
                      : scoreCardTexts.READ}
                    {data.count}
                  </Typography>
                  <Typography variant='body'>
                    {scoreCardTexts.ASSIGNED}
                    {data.total}
                  </Typography>
                  <LinearProgress
                    variant='determinate'
                    value={data.progress}
                    className={classes.customLinearProgress}
                    sx={{
                      mt: 2,
                      color: AppColors.PRIMARY,
                      bgColor: AppColors.SECONDARY,
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          ))}
      </Box>
    </>
  );
};

export default ScoreCard;
